<script setup lang="ts">
import { useField } from 'vee-validate';
import { computed, nextTick, onMounted, reactive, ref, watch } from 'vue';

import BaseCalendar from 'lib/components/calendar/BaseCalendar.vue';
import { FieldValidation } from 'lib/components/form/Form.vue.d';

import { CalendarEmits, CalendarProps } from './Calendar.vue.d';

const props = withDefaults(defineProps<CalendarProps>(), {
  view: 'date',
  mode: 'single',
  showYear: true,
  showOptionalText: undefined,
});

const emit = defineEmits<CalendarEmits>();

onMounted(() => {
  setValidator();
  if (props.mode === 'range') date.value = [];
  if (props.dateValue && props.useValidator) {
    date.value = parseDateFromProps();
    field.value = props.dateValue;
  }
});

const date = ref<Date | Date[]>();
const field = reactive<FieldValidation<typeof props.modelValue>>({
  value: undefined,
});

const invalidInput = computed(() => props.invalid || !!field.errorMessage);

const getGMTTime = (dateString: Date | string): Date => {
  return new Date(new Date(dateString).toUTCString());
};

/**
 * This function will parse the date number/timestamp into date string that can be displayed on date input.
 */
const parseDateFromProps = (): Date | Date[] | undefined => {
  if (!Array.isArray(props.dateValue))
    return props.dateValue ? new Date(props.dateValue) : undefined;

  return props.dateValue.map((timeStamp) =>
    timeStamp ? new Date(timeStamp) : null,
  );
};

const parseDate = (dateToParse: Date | Date[]): number | number[] => {
  if (
    (props.exactSelection || props.mode === 'multiple') &&
    Array.isArray(dateToParse)
  ) {
    return dateToParse
      .filter(Boolean)
      .map((each) => getGMTTime(each).getTime());
  }

  if (!Array.isArray(dateToParse)) return getGMTTime(dateToParse).getTime();

  return [
    getGMTTime(dateToParse[0]).getTime(),
    getGMTTime(dateToParse[1] ?? dateToParse[0]).setHours(23, 59, 59),
  ];
};

const setValidator = (): void => {
  if (props.useValidator) {
    Object.assign(
      field,
      useField(props.fieldName ?? 'date', (value: string) => {
        return setValidatorMessage(value);
      }),
    );
  }
};

const onUpdateModelValue = (ev?: Date | Date[]): void => {
  date.value = ev;
  const parsedDate = date.value != null ? parseDate(date.value) : undefined;
  emit('update:modelValue', parsedDate);
  field.value = parsedDate;
};

const setValidatorMessage = async (
  value: string,
): Promise<boolean | string> => {
  await nextTick();

  if (!value && props.mandatory) {
    return props.customValidation?.empty ?? 'Tanggal tidak boleh kosong';
  } else if (props.validatorMessage && props.invalid) {
    return props.validatorMessage;
  }

  return true;
};

watch(
  () => props.dateValue,
  () => {
    date.value = parseDateFromProps();
    field.value = props.dateValue;
  },
  { once: true },
);

watch(
  () => props.modelValue,
  () => {
    if (!Array.isArray(props.modelValue)) {
      date.value = props.modelValue ? new Date(props.modelValue) : undefined;
    } else {
      date.value = props.modelValue.map((timeStamp) =>
        timeStamp ? new Date(timeStamp) : null,
      );
    }
    field.value = props.modelValue;
  },
);
</script>

<template>
  <BaseCalendar
    v-bind="props"
    :error-message="field.errorMessage"
    :hide-on-date-time-select="false"
    :hide-on-range-selection="!showButtons && !showTime"
    :invalid="invalidInput"
    :manual-input="false"
    :model-value="date"
    :placeholder="placeholder ?? 'Pilih tanggal'"
    :selection-mode="mode"
    @update:model-value="onUpdateModelValue"
  >
    <template #default="{ onClick }">
      <slot :on-click="onClick" />
    </template>

    <template #addon-overlay>
      <slot name="addon-overlay" />
    </template>
  </BaseCalendar>
</template>
